// src/Features/API/apiSlice.js
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from './api';
import { baseQueryWithReauth } from './baseQueryWithReauth';
import { getToken } from '../../shared/utils/tokenStorage';
import { parseContentDispositionFilename } from '../../shared/utils/fileDownload';

// Define the base URL for your Django backend
const BASE_URL = getApiBaseUrl();

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [
    'DeviceGroup',
    'Vehicle',
    'Device',
    // Separate from 'Device' on purpose: the Comms list is ~250 rows, while the
    // 'Device' cache is the full 15,900-device payload. Sharing a tag would mean
    // every Comms edit refetched the whole fleet, and every bulk device move
    // refetched Comms.
    'CommsDevice',
    'Alert',
    'Event',
    'User',
    'UserGroup',
    'SalesReps',
    'SalesTeam',
    'Branches',
    'Technicians',
    'Test',
    'Client',
    'Task',
    'Geofences',
    'SuppressionProfile',
    'DeviceOverride',
    'SuppressionStats',
    'SuppressedAlertGroups',
    'SuppressedGroupDetail',
    'Template',
    'TemplateStats',
    'TemplateIndustries',
    'TemplateCategories',
    'TemplateUsage',
    'TemplateAnalytics',
    'AgentPresence',
    'AgentPresenceSummary',
    'QuickPresenceStats',
    'UserActivity',
    'ActivityTimeline',
    'TempTest',
    'DeviceState',
    'GlobalSuppressionProfile',
    'AlertDisable',
    'AlertTypes',
    'VMSAlerts',
    'RecentlyDiscardedAlerts',
  ],
  endpoints: (builder) => ({
    // ========== FLEET MANAGEMENT ENDPOINTS (fleet/) ==========
    fetchDeviceGroups: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.group_by_org) queryParams.append('group_by_org', 'true');
        if (params.user_orgs_only) queryParams.append('user_orgs_only', 'true');
        if (params.organization_id) queryParams.append('organization_id', params.organization_id);
        const queryString = queryParams.toString();
        return `fleet/device-groups/${queryString ? `?${queryString}` : ''}`;
      },
      // Keep cached data for 120s after unmount so tab switches / route changes
      // don't trigger a full refetch. WebSocket invalidations still work instantly.
      keepUnusedDataFor: 120,
      // Fine-grained cache tags for selective invalidation
      // Use `id` (Django PK) as primary identifier to avoid ID collision
      providesTags: (result, error, params) => {
        // Handle org-grouped structure
        if (params?.group_by_org && result?.organizations) {
          const tags = result.organizations.flatMap((org) =>
            org.groups.map((group) => ({ type: 'DeviceGroup', id: group.id }))
          );
          return [...tags, { type: 'DeviceGroup', id: 'LIST' }];
        }
        // Handle flat list (backward compatible)
        return result
          ? [
              ...result.map((group) => ({ type: 'DeviceGroup', id: group.id || group.group_id })),
              { type: 'DeviceGroup', id: 'LIST' },
            ]
          : [{ type: 'DeviceGroup', id: 'LIST' }];
      },
    }),
    fetchVehiclesForGroups: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.group_id) queryParams.append('group_id', params.group_id);
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.search) queryParams.append('search', params.search);
        const qs = queryParams.toString();
        return `fleet/vehicles/${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Vehicle'],
    }),
    fetchDevicesForVehicles: builder.query({
      query: () => 'fleet/devices/',
      providesTags: ['Device'],
    }),
    // Fetch vehicles + unassigned devices for a specific group (for test wizard)
    fetchGroupVehicles: builder.query({
      query: (groupId) => `fleet/groups/${groupId}/vehicles/`,
      providesTags: ['Vehicle', 'Device'],
    }),
    // Fetch only unassigned devices (no vehicle) - for Add Group wizard
    fetchUnassignedDevices: builder.query({
      query: () => 'fleet/devices/?unassigned=true',
      providesTags: ['Device'],
    }),
    updateClient: builder.mutation({
      query: ({ group_id, data }) => ({
        url: `fleet/device-groups/${group_id}/`,
        method: 'PUT',
        body: data,
      }),
      // Invalidate specific group and the list
      invalidatesTags: (result, error, { group_id }) => [
        { type: 'DeviceGroup', id: group_id },
        { type: 'DeviceGroup', id: 'LIST' },
      ],
    }),
    deleteClient: builder.mutation({
      query: (group_id) => ({
        url: `fleet/device-groups/${group_id}/`,
        method: 'DELETE',
      }),
      // Invalidate specific group and the list
      invalidatesTags: (result, error, group_id) => [
        { type: 'DeviceGroup', id: group_id },
        { type: 'DeviceGroup', id: 'LIST' },
      ],
    }),
    moveDeviceToGroup: builder.mutation({
      query: ({ device_id, new_group_id }) => ({
        url: `fleet/move-device/${device_id}/`,
        method: 'POST',
        body: { new_group_id },
      }),
      invalidatesTags: ['DeviceGroup', 'Device'],
    }),
    // Move addressed by primary key. Prefer this over moveDeviceToGroup: that
    // route resolves through DeviceResolver (external_vms_id before id), and it
    // cannot address the 9,655 devices whose device_id is NULL — for those the
    // old call built `fleet/move-device/null/`, which matched no route at all.
    moveDeviceToGroupByPk: builder.mutation({
      query: ({ pk, new_group_id }) => ({
        url: `fleet/devices/${pk}/move-to-group/`,
        method: 'POST',
        body: { new_group_id },
      }),
      invalidatesTags: ['DeviceGroup', 'Device'],
    }),
    createNewDevice: builder.mutation({
      query: (deviceData) => ({
        url: 'fleet/create-new-device/',
        method: 'POST',
        body: deviceData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device'],
    }),
    clientOnboarding: builder.mutation({
      query: (payload) => ({
        url: 'fleet/client-onboarding/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [{ type: 'DeviceGroup', id: 'LIST' }, { type: 'Device', id: 'LIST' }],
    }),
    createGroup: builder.mutation({
      query: (groupData) => ({
        url: 'fleet/create-group/',
        method: 'POST',
        body: groupData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [{ type: 'DeviceGroup', id: 'LIST' }],
    }),
    fetchCmsParentOrgs: builder.query({
      query: () => 'fleet/cms-parent-orgs/',
      keepUnusedDataFor: 300,
    }),
    createLocalGroup: builder.mutation({
      query: (groupData) => ({
        url: 'fleet/create-local-group/',
        method: 'POST',
        body: groupData,
        headers: { 'Content-Type': 'application/json' },
      }),
      // Optimistic update - show new group immediately in UI
      async onQueryStarted(groupData, { dispatch, queryFulfilled }) {
        // Add optimistic entry to cache
        const patchResult = dispatch(
          apiSlice.util.updateQueryData('fetchDeviceGroups', undefined, (draft) => {
            draft.unshift({
              id: `temp-${Date.now()}`,
              group_id: null,
              title: groupData.title,
              vehicles: [],
              unassigned_devices: [],
              is_local: true,
              _optimistic: true, // Flag for UI styling
            });
          })
        );
        try {
          const { data } = await queryFulfilled;
          // Replace optimistic entry with real data from server
          dispatch(
            apiSlice.util.updateQueryData('fetchDeviceGroups', undefined, (draft) => {
              const idx = draft.findIndex((g) => g._optimistic);
              if (idx !== -1 && data?.group) {
                draft[idx] = data.group;
              }
            })
          );
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: 'DeviceGroup', id: 'LIST' }],
    }),
    assignDeviceToGroup: builder.mutation({
      query: (payload) => ({
        url: 'fleet/assign-device-to-group/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device'],
    }),
    updateVehicle: builder.mutation({
      query: (vehicleData) => ({
        url: 'fleet/update-vehicles/',
        method: 'POST',
        body: vehicleData,
      }),
      invalidatesTags: ['Vehicle', 'Device'],
    }),
    editVehicle: builder.mutation({
      query: ({ vehicleId, ...data }) => ({
        url: `fleet/vehicles/${vehicleId}/edit/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Vehicle', 'DeviceGroup', 'Device'],
    }),
    deleteVehicle: builder.mutation({
      query: (vehicleId) => ({
        url: `fleet/vehicles/${vehicleId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vehicle', 'DeviceGroup', 'Device'],
    }),
    updateDevice: builder.mutation({
      query: (deviceData) => ({
        url: 'fleet/update-device/',
        method: 'POST',
        body: deviceData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),
    updateDeviceExternalFields: builder.mutation({
      query: (deviceData) => ({
        url: 'fleet/devices/update-external-fields/',
        method: 'POST',
        body: deviceData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device'],
    }),
    localAddDevices: builder.mutation({
      query: (devices) => ({
        url: 'fleet/create-device/',
        method: 'POST',
        body: devices,
      }),
      invalidatesTags: ['Device'],
    }),
    createDeviceSameGroup: builder.mutation({
      query: (payload) => ({
        url: 'fleet/create-device-same-group/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device'],
    }),
    associateDeviceWithVehicle: builder.mutation({
      query: (payload) => ({
        url: 'fleet/associate-device-with-vehicle/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),
    createVehicle: builder.mutation({
      query: (newVehicleData) => ({
        url: 'fleet/vehicles/create/',
        method: 'POST',
        body: newVehicleData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Vehicle'],
    }),
    assignDeviceToVehicle: builder.mutation({
      query: (payload) => ({
        url: 'fleet/devices/assign-to-vehicle/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),
    fetchGeofences: builder.query({
      query: () => 'fleet/geofences/',
      providesTags: ['Geofences'],
    }),
    createGeofence: builder.mutation({
      query: (data) => ({
        url: 'fleet/geofences/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Geofences'],
    }),
    updateGeofence: builder.mutation({
      query: ({ id, data }) => ({
        url: `fleet/geofences/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Geofences'],
    }),
    deleteGeofence: builder.mutation({
      query: (id) => ({
        url: `fleet/geofences/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Geofences'],
    }),
    fetchVehicleEventHistory: builder.query({
      query: ({ vehicleId, eventType, limit = 3 }) => {
        if (!vehicleId) {
          return { url: 'monitoring/events/list/', params: { page_size: 0 } };
        }

        let url = `fleet/vehicles/${vehicleId}/event-history/`;
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (limit) params.append('limit', limit);
        const queryString = params.toString();
        if (queryString) url += `?${queryString}`;
        return url;
      },
      providesTags: ['Events'],
    }),

    // Dynamic Wizard Device Management
    fetchAvailableDevices: builder.query({
      query: (params) => ({
        url: 'fleet/devices/available/',
        params,
      }),
      providesTags: ['Device'],
    }),
    quickCreateDevice: builder.mutation({
      query: (deviceData) => ({
        url: 'fleet/devices/quick-create/',
        method: 'POST',
        body: deviceData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'DeviceGroup'],
    }),
    transferDevice: builder.mutation({
      query: (transferData) => ({
        url: 'fleet/devices/transfer/',
        method: 'POST',
        body: transferData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'DeviceGroup', 'Vehicle'],
    }),
    moveDeviceToDeinstalled: builder.mutation({
      query: (deinstallData) => ({
        url: 'fleet/devices/move-to-deinstalled/',
        method: 'POST',
        body: deinstallData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'DeviceGroup', 'Vehicle'],
    }),
    deinstallDevice: builder.mutation({
      query: (deinstallData) => ({
        url: 'fleet/devices/deinstall/',
        method: 'POST',
        body: deinstallData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'DeviceGroup', 'Vehicle'],
    }),
    // Delete device (soft delete - moves to DE-INSTALLED group)
    deleteDevice: builder.mutation({
      // Accepts `pk` (preferred, unambiguous) or the legacy `device_id`.
      query: ({ pk, device_id }) => ({
        url: 'fleet/devices/delete/',
        method: 'POST',
        body: pk !== undefined ? { pk } : { device_id },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'DeviceGroup', 'Vehicle'],
    }),
    // Bulk device operations (move, deinstall, delete)
    bulkDeviceOperation: builder.mutation({
      // `device_pks` are Django primary keys, matched exactly by the backend.
      // The legacy `device_ids` path resolves device_id first, and 5,002 PKs
      // collide with another device's device_id — passing PKs there acts on the
      // wrong devices.
      query: ({ device_pks, device_ids, operation, target_group_id }) => ({
        url: 'fleet/devices/bulk/',
        method: 'POST',
        body: {
          ...(device_pks ? { device_pks } : { device_ids }),
          operation,
          target_group_id,
        },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'DeviceGroup', 'Vehicle'],
    }),

    // ========== COMMS ENDPOINTS (fleet/comms/) ==========
    // The Communicator / FleetTrack Lite family and the contact details an
    // operator maintains by hand.
    //
    // Deliberately its own endpoint rather than filtering the general device
    // list client-side: that list is the full 15,900-device / ~6 MB payload, and
    // pulling it to show ~250 rows is the exact problem the Orgs & Fleet
    // remediation exists to remove.
    fetchCommsDevices: builder.query({
      query: (params = {}) => {
        const qp = new URLSearchParams();
        if (params.search) qp.append('search', params.search);
        if (params.page) qp.append('page', params.page);
        if (params.page_size) qp.append('page_size', params.page_size);
        if (params.ordering) qp.append('ordering', params.ordering);
        if (params.organization) qp.append('organization', params.organization);
        if (params.group) qp.append('group', params.group);
        if (params.state) qp.append('state', params.state);
        // The grid's filter chips, server-side. 'missing_phone' predates them.
        if (params.phone) qp.append('phone', params.phone);
        if (params.assignment) qp.append('assignment', params.assignment);
        if (params.missing_phone) qp.append('missing_phone', 'true');
        const qs = qp.toString();
        return `fleet/comms/devices/${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) => [
        { type: 'CommsDevice', id: 'LIST' },
        ...(result?.results ?? []).map((d) => ({ type: 'CommsDevice', id: d.id })),
      ],
      keepUnusedDataFor: 120,
    }),
    updateCommsDevice: builder.mutation({
      // Addressed by primary key — the general update endpoint resolves
      // device_id first, which is NULL for 9,655 devices and collides with
      // another device's PK for 5,002 more.
      query: ({ id, ...fields }) => ({
        url: `fleet/comms/devices/${id}/`,
        method: 'PATCH',
        body: fields,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'CommsDevice', id: arg.id },
        { type: 'CommsDevice', id: 'LIST' },
        // Only a device_type change is visible on the Devices tab; phone, SIM
        // and note are Comms-only, so they must not invalidate the full
        // 15,900-device cache. Checked against what was SENT — the response
        // always echoes device_type, changed or not.
        ...('device_type' in arg ? ['Device'] : []),
      ],
    }),

    // The column catalogue and the file itself share one URL: GET describes what
    // can be exported, POST returns it. The picker is therefore built from the
    // server's list and cannot offer a column the exporter cannot write.
    fetchCommsExportCatalogue: builder.query({
      query: () => 'fleet/comms/devices/export/',
      keepUnusedDataFor: 600,
    }),
    exportCommsDevices: builder.mutation({
      query: ({ params = {}, body }) => {
        const qp = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') qp.append(key, value);
        });
        const qs = qp.toString();
        return {
          url: `fleet/comms/devices/export/${qs ? `?${qs}` : ''}`,
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
          // NOT the string 'blob': RTK Query's handler union is
          // 'content-type' | 'json' | 'text' | function, and anything else falls
          // through to response.text(). A CSV would survive that; an .xlsx or a
          // .pdf would be decoded as UTF-8 and arrive corrupt. Errors stay text
          // so the toast can read them.
          responseHandler: (response) => (response.ok ? response.blob() : response.text()),
        };
      },
      // meta carries the raw Response, the only place the filename is readable.
      transformResponse: (blob, meta) => ({
        blob,
        filename: parseContentDispositionFilename(
          meta?.response?.headers?.get('content-disposition')
        ),
      }),
      // A read that happens to be a POST: it must not churn the device cache.
    }),

    // ========== MONITORING ENDPOINTS (monitoring/) ==========
    fetchAlerts: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/alerts/',
        params: {
          include_suppressed: true,
          include_profile_context: true,
          ...params,
        },
      }),
      providesTags: ['Alert'],
    }),
    fetchAlertsByDevice: builder.query({
      query: (device_id) => `monitoring/alerts/device/${device_id}/`,
      providesTags: ['Alert'],
    }),
    createEvent: builder.mutation({
      query: (eventData) => ({
        url: 'monitoring/events/',
        method: 'POST',
        body: eventData,
        formData: true,
      }),
      invalidatesTags: ['Events', 'Event', 'Alert'], // Also invalidate Alert cache since creating an event resolves the alert
    }),
    fetchEvents: builder.query({
      query: (queryString) =>
        queryString ? `monitoring/events/list/?${queryString}` : 'monitoring/events/list/',
      providesTags: ['Event'],
    }),
    fetchEventDetails: builder.query({
      query: (event_id) => `monitoring/events/${event_id}/`,
      providesTags: (result, error, id) => [{ type: 'Event', id }],
    }),
    resolveEvent: builder.mutation({
      query: ({ id, comments }) => ({
        url: `monitoring/events/${id}/resolve/`,
        method: 'POST',
        body: { resolution_comments: comments },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Event', id }, 'Event', 'Alert'],
    }),
    assignEvent: builder.mutation({
      query: ({ id, userId }) => ({
        url: `monitoring/events/${id}/assign/`,
        method: 'POST',
        body: { user_id: userId },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Event', id }, 'Event'],
    }),
    updateEvent: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `monitoring/events/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Event', id }, 'Event'],
    }),

    resolveAlert: builder.mutation({
      query: (formData) => ({
        url: 'monitoring/resolve-alert/',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['Alert', 'Event'],
    }),
    updateAlertStatus: builder.mutation({
      query: ({ alert_id, status }) => ({
        url: `monitoring/alerts/${alert_id}/status/`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Alert'],
    }),
    investigateAlert: builder.mutation({
      query: (data) => ({
        url: 'monitoring/investigate-alert/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Alert'],
    }),
    discardAlertConsolidated: builder.mutation({
      query: (data) => ({
        url: 'monitoring/alerts/discard/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Alert', 'SuppressedAlertGroups', 'RecentlyDiscardedAlerts'],
    }),
    // Bulk alert actions (delete resolved, delete all, etc.)
    bulkAlertAction: builder.mutation({
      query: (data) => ({
        url: 'monitoring/alerts/bulk-action/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Alert', 'SuppressedAlertGroups'],
    }),
    // ========== SUPPRESSED ALERTS ENDPOINTS ==========
    fetchSuppressedAlerts: builder.query({
      query: (filters = {}) => ({
        url: 'monitoring/alerts/',
        params: {
          include_suppressed: true,
          only_suppressed: filters.only_suppressed || false,
          limit: filters.limit || 100,
          ...filters,
        },
      }),
      providesTags: ['Alert'],
    }),
    fetchSuppressedAlertDetails: builder.query({
      query: (alertId) => `monitoring/suppressed-alerts/${alertId}/`,
      providesTags: (result, error, id) => [{ type: 'Alert', id }],
    }),
    unsuppressAlert: builder.mutation({
      query: ({ alertId, reason }) => ({
        url: `monitoring/suppressed-alerts/${alertId}/`,
        method: 'PATCH',
        body: { action: 'unsuppress', reason },
      }),
      invalidatesTags: ['Alert'],
    }),
    resetSuppressionGroup: builder.mutation({
      query: ({ alertId, reason }) => ({
        url: `monitoring/suppressed-alerts/${alertId}/actions/`,
        method: 'POST',
        body: { action: 'reset', reason },
      }),
      invalidatesTags: ['Alert'],
    }),
    resolveSupressedAlert: builder.mutation({
      query: ({ alertId, reason }) => ({
        url: `monitoring/suppressed-alerts/${alertId}/actions/`,
        method: 'POST',
        body: { action: 'resolve', reason },
      }),
      invalidatesTags: ['Alert'],
    }),

    // ========== ENHANCED SUPPRESSION MANAGEMENT ENDPOINTS ==========
    // User Suppression Profile Management
    fetchUserSuppressionProfiles: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/user-suppression-profiles/',
        params,
      }),
      providesTags: ['SuppressionProfile'],
    }),
    fetchUserSuppressionProfile: builder.query({
      query: (id) => `monitoring/user-suppression-profiles/${id}/`,
      providesTags: (result, error, id) => [{ type: 'SuppressionProfile', id }],
    }),
    createUserSuppressionProfile: builder.mutation({
      query: (data) => ({
        url: 'monitoring/user-suppression-profiles/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SuppressionProfile', 'Alert'],
    }),
    updateUserSuppressionProfile: builder.mutation({
      query: ({ id, data }) => ({
        url: `monitoring/user-suppression-profiles/${id}/`,
        method: 'PUT',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SuppressionProfile', 'Alert'],
    }),
    deleteUserSuppressionProfile: builder.mutation({
      query: (id) => ({
        url: `monitoring/user-suppression-profiles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SuppressionProfile', 'Alert'],
    }),

    // Device Suppression Overrides
    fetchDeviceSuppressionOverrides: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/device-suppression-overrides/',
        params,
      }),
      providesTags: ['DeviceOverride'],
    }),
    fetchDeviceSuppressionOverride: builder.query({
      query: (id) => `monitoring/device-suppression-overrides/${id}/`,
      providesTags: (result, error, id) => [{ type: 'DeviceOverride', id }],
    }),
    createDeviceSuppressionOverride: builder.mutation({
      query: (data) => ({
        url: 'monitoring/device-suppression-overrides/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['DeviceOverride', 'Alert'],
    }),
    updateDeviceSuppressionOverride: builder.mutation({
      query: ({ id, data }) => ({
        url: `monitoring/device-suppression-overrides/${id}/`,
        method: 'PUT',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['DeviceOverride', 'Alert'],
    }),
    deleteDeviceSuppressionOverride: builder.mutation({
      query: (id) => ({
        url: `monitoring/device-suppression-overrides/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DeviceOverride', 'Alert'],
    }),

    // Enhanced Suppression Stats and Analytics
    fetchSuppressionEffectivenessStats: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppression/effectiveness/',
        params,
      }),
      providesTags: ['SuppressionStats'],
    }),
    fetchSuppressionStats: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppression/stats/',
        params,
      }),
      providesTags: ['SuppressionStats'],
    }),
    fetchSuppressionAnalytics: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppression/analytics/',
        params: {
          all_time: true,
          include_breakdown: true,
          ...params,
        },
      }),
      providesTags: ['SuppressionAnalytics'],
    }),

    // ========== UNIFIED SUPPRESSION SYSTEM ENDPOINTS ==========
    fetchSuppressionProfiles: builder.query({
      query: () => 'monitoring/suppression-profiles/',
      providesTags: ['UnifiedSuppressionProfile'],
    }),
    activateSuppressionProfile: builder.mutation({
      query: (data) => ({
        url: 'monitoring/activate-suppression-profile/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['UnifiedSuppressionProfile', 'Alert'],
    }),
    fetchUnifiedSuppressionStats: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/unified-suppression-stats/',
        params,
      }),
      providesTags: ['UnifiedSuppressionStats'],
    }),
    fetchSuppressionHealth: builder.query({
      query: () => 'monitoring/suppression-health/',
      providesTags: ['SuppressionHealth'],
    }),
    testSuppressionRules: builder.mutation({
      query: (data) => ({
        url: 'monitoring/test-suppression-rules/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    fetchSuppressionDecisionLogs: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppression-decision-logs/',
        params,
      }),
      providesTags: ['SuppressionDecisionLogs'],
    }),

    // ========== GLOBAL SUPPRESSION PROFILE MANAGEMENT ==========
    fetchGlobalSuppressionProfiles: builder.query({
      query: () => 'monitoring/global-suppression-profiles/',
      providesTags: ['GlobalSuppressionProfile'],
    }),
    fetchGlobalSuppressionProfile: builder.query({
      query: (id) => `monitoring/global-suppression-profiles/${id}/`,
      providesTags: (result, error, id) => [{ type: 'GlobalSuppressionProfile', id }],
    }),
    updateGlobalSuppressionProfile: builder.mutation({
      query: ({ id, data }) => ({
        url: `monitoring/global-suppression-profiles/${id}/`,
        method: 'PUT',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['GlobalSuppressionProfile', 'SuppressionProfile', 'Alert'],
    }),
    setGlobalDefaultProfile: builder.mutation({
      query: (profileId) => ({
        url: 'monitoring/set-global-default-profile/',
        method: 'POST',
        body: { profile_id: profileId },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['GlobalSuppressionProfile', 'SuppressionProfile'],
    }),

    // ========== ALERT DISABLE (DEVICE/VEHICLE TEMPORARY DISABLE) ==========
    fetchAlertDisables: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/alert-disable/',
        params,
      }),
      providesTags: ['AlertDisable'],
    }),
    fetchAlertDisable: builder.query({
      query: (id) => `monitoring/alert-disable/${id}/`,
      providesTags: (result, error, id) => [{ type: 'AlertDisable', id }],
    }),
    createAlertDisable: builder.mutation({
      query: (data) => ({
        url: 'monitoring/alert-disable/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['AlertDisable', 'Alert'],
    }),
    updateAlertDisable: builder.mutation({
      query: ({ id, data }) => ({
        url: `monitoring/alert-disable/${id}/`,
        method: 'PATCH',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['AlertDisable', 'Alert'],
    }),
    deleteAlertDisable: builder.mutation({
      query: (id) => ({
        url: `monitoring/alert-disable/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AlertDisable', 'Alert'],
    }),

    // ========== ALERT TYPES ==========
    fetchAlertTypes: builder.query({
      query: () => 'monitoring/alert-types/',
      providesTags: ['AlertTypes'],
    }),

    // ========== VMS ALERTS (READ-ONLY) ==========
    fetchVMSAlerts: builder.query({
      query: (params = {}) => ({
        url: 'fleet/vms-alerts/',
        params,
      }),
      providesTags: ['VMSAlerts'],
    }),
    fetchVMSAlertDetail: builder.query({
      query: (alertId) => `fleet/vms-alerts/${alertId}/`,
      providesTags: (result, error, alertId) => [{ type: 'VMSAlerts', id: alertId }],
    }),

    // ========== ALERT GROUPING ENDPOINTS ==========
    fetchRecentlyDiscardedAlerts: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/alerts/recently-discarded/',
        params: {
          page: params.page || 1,
          page_size: params.page_size || 5,
          search: params.search || '',
        },
      }),
      providesTags: ['RecentlyDiscardedAlerts'],
    }),
    fetchSuppressedAlertGroups: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppressed-alerts/groups/',
        params: {
          page: params.page || 1,
          page_size: params.page_size || 5,
          use_cache: params.use_cache !== false,
          ...params,
        },
      }),
      providesTags: ['SuppressedAlertGroups'],
    }),
    fetchSuppressedGroupDetail: builder.query({
      query: ({ group_key, ...params }) => ({
        url: `monitoring/suppressed-alerts/groups/${group_key}/`,
        params: {
          page: params.page || 1,
          page_size: params.page_size || 50,
          use_cache: params.use_cache !== false,
          ...params,
        },
      }),
      providesTags: (result, error, { group_key }) => [
        { type: 'SuppressedGroupDetail', id: group_key },
      ],
    }),
    suppressedGroupBulkAction: builder.mutation({
      query: (data) => {
        // Validate input data
        if (!data.action) {
          throw new Error('Action is required for bulk operations');
        }
        if (!data.alert_ids || !Array.isArray(data.alert_ids) || data.alert_ids.length === 0) {
          throw new Error('Valid alert_ids array is required');
        }
        if (data.alert_ids.length > 100) {
          throw new Error(
            'Cannot process more than 100 alerts at once. Use chunking on the frontend.'
          );
        }

        return {
          url: 'monitoring/suppressed-alerts/groups/bulk-action/',
          method: 'POST',
          body: data,
          headers: { 'Content-Type': 'application/json' },
        };
      },
      invalidatesTags: ['SuppressedAlertGroups', 'Alert', 'SuppressedGroupDetail'],
      transformErrorResponse: (response, meta, arg) => {
        console.error('🚨 suppressedGroupBulkAction error:', {
          status: response.status,
          data: response.data,
          arg: arg,
        });

        // Enhanced error handling
        if (response.status === 400) {
          if (response.data?.error?.includes('100 alerts')) {
            return {
              status: response.status,
              data: response.data,
              message: 'Too many alerts selected. Please use automatic chunking.',
              shouldChunk: true,
            };
          }
          return {
            status: response.status,
            data: response.data,
            message: response.data?.error || 'Invalid request parameters',
          };
        }

        if (response.status === 404) {
          return {
            status: response.status,
            data: response.data,
            message: 'Group not found - it may have been processed already',
            shouldRefresh: true,
          };
        }

        if (response.status >= 500) {
          return {
            status: response.status,
            data: response.data,
            message: 'Server error - please try again later',
            shouldRetry: true,
          };
        }

        return {
          status: response.status,
          data: response.data,
          message: response.data?.error || 'An unexpected error occurred',
        };
      },
    }),
    bulkDiscardSuppressedGroup: builder.mutation({
      query: (data) => {
        // Validate input data
        if (!data.alert_ids || !Array.isArray(data.alert_ids) || data.alert_ids.length === 0) {
          throw new Error('Valid alert_ids array is required');
        }
        if (data.alert_ids.length > 100) {
          throw new Error(
            'Cannot process more than 100 alerts at once. Use chunking on the frontend.'
          );
        }

        return {
          url: 'monitoring/suppressed-alerts/groups/discard/',
          method: 'POST',
          body: data,
          headers: { 'Content-Type': 'application/json' },
        };
      },
      invalidatesTags: ['SuppressedAlertGroups', 'Alert', 'SuppressedGroupDetail'],
      transformErrorResponse: (response, meta, arg) => {
        console.error('🚨 bulkDiscardSuppressedGroup error:', {
          status: response.status,
          data: response.data,
          arg: arg,
        });

        // Enhanced error handling
        if (response.status === 400) {
          if (response.data?.error?.includes('100 alerts')) {
            return {
              status: response.status,
              data: response.data,
              message: 'Too many alerts selected. Please use automatic chunking.',
              shouldChunk: true,
            };
          }
          return {
            status: response.status,
            data: response.data,
            message: response.data?.error || 'Invalid request parameters',
          };
        }

        if (response.status === 404) {
          return {
            status: response.status,
            data: response.data,
            message: 'Group not found - it may have been processed already',
            shouldRefresh: true,
          };
        }

        if (response.status >= 500) {
          return {
            status: response.status,
            data: response.data,
            message: 'Server error - please try again later',
            shouldRetry: true,
          };
        }

        return {
          status: response.status,
          data: response.data,
          message: response.data?.error || 'An unexpected error occurred',
        };
      },
    }),
    enhancedBulkDiscardSuppressedGroup: builder.mutation({
      queryFn: async (data, { dispatch, getState }) => {
        const { chunkArrayForBulkAction, processChunksSequentially, retryWithExponentialBackoff } =
          await import('../../portals/bureau/utils/chunkArrayBySize');

        try {
          console.log('🗑️ Starting enhanced bulk discard group operation with data:', data);

          // Step 1: Validate group key and fetch alert IDs if only group_key is provided
          let alertIds = data.alert_ids || [];

          if (data.group_key && alertIds.length === 0) {
            console.log('🔍 No alert_ids provided, fetching from group_key:', data.group_key);

            const validationResult = await dispatch(
              apiSlice.endpoints.validateSuppressedGroup.initiate(data.group_key)
            );

            if (validationResult.error) {
              console.error('❌ Group key validation failed:', validationResult.error);
              throw new Error(`Group validation failed: ${validationResult.error.error}`);
            }

            console.log('✅ Group key validated successfully:', validationResult.data);

            // Extract alert IDs from the group data
            if (validationResult.data?.data?.alerts) {
              alertIds = validationResult.data.data.alerts.map(
                (alert) => alert.alert_id || alert.id
              );
              console.log(`📋 Extracted ${alertIds.length} alert IDs from group data`);
            }

            // Update alert count from validated data if available
            if (
              validationResult.data?.alertCount &&
              validationResult.data.alertCount !== data.alert_count
            ) {
              console.log(
                `📊 Updated alert count from ${data.alert_count} to ${validationResult.data.alertCount}`
              );
              data.alert_count = validationResult.data.alertCount;
            }
          } else if (data.group_key) {
            // Still validate the group key if provided along with alert_ids
            const validationResult = await dispatch(
              apiSlice.endpoints.validateSuppressedGroup.initiate(data.group_key)
            );

            if (validationResult.error) {
              console.error('❌ Group key validation failed:', validationResult.error);
              throw new Error(`Group validation failed: ${validationResult.error.error}`);
            }

            console.log('✅ Group key validated successfully');
          }

          // Step 2: Check if we need chunking

          console.log('🔍 Enhanced bulk discard - data validation:', {
            data,
            alertIds,
            alertIdsLength: alertIds.length,
            alertIdsType: typeof alertIds,
            isArray: Array.isArray(alertIds),
          });

          if (!Array.isArray(alertIds) || alertIds.length === 0) {
            if (data.group_key) {
              throw new Error(
                'No alerts found in the specified group. The group may be empty or the group_key is invalid.'
              );
            } else {
              throw new Error('Valid alert_ids array is required for enhanced bulk discard');
            }
          }

          if (alertIds.length > 100) {
            console.log(`🔄 Processing ${alertIds.length} alerts in chunks (max 100 per chunk)`);

            // Chunk the alert IDs
            const chunks = chunkArrayForBulkAction(alertIds, 100);

            // Process chunks with retry logic
            const chunkResults = await processChunksSequentially(
              chunks,
              async (chunkAlertIds, chunkIndex) => {
                console.log(
                  `📦 Processing chunk ${chunkIndex + 1}/${chunks.length} with ${chunkAlertIds.length} alerts`
                );

                return await retryWithExponentialBackoff(
                  async () => {
                    const result = await dispatch(
                      apiSlice.endpoints.bulkDiscardSuppressedGroup.initiate({
                        ...data,
                        alert_ids: chunkAlertIds,
                        reason: `${data.discard_reason || data.reason || 'Bulk discard'} (chunk ${chunkIndex + 1}/${chunks.length})`,
                      })
                    );

                    if (result.error) {
                      throw result.error;
                    }

                    return result.data;
                  },
                  {
                    maxRetries: 3,
                    initialDelay: 1000,
                    maxDelay: 10000,
                  }
                );
              },
              {
                onProgress: (progressInfo) => {
                  console.log(
                    `📈 Chunk progress: ${progressInfo.progress.toFixed(1)}% (${progressInfo.chunkIndex}/${progressInfo.totalChunks})`
                  );
                },
                onChunkError: (error, chunkIndex, chunk) => {
                  console.error(`❌ Error processing chunk ${chunkIndex + 1}:`, error);
                },
                continueOnError: true,
              }
            );

            console.log('✅ Chunked bulk discard completed:', {
              totalChunks: chunkResults.totalChunks,
              successfulChunks: chunkResults.successfulChunks,
              failedChunks: chunkResults.failedChunks,
              totalAlerts: alertIds.length,
            });

            // Return summary of chunked operation
            return {
              data: {
                success: true,
                chunked: true,
                ...chunkResults,
                totalAlertsProcessed: data.alert_count,
              },
            };
          } else {
            // Step 3: Process normally for small groups with retry
            const result = await retryWithExponentialBackoff(
              async () => {
                const result = await dispatch(
                  apiSlice.endpoints.bulkDiscardSuppressedGroup.initiate({
                    ...data,
                    alert_ids: alertIds,
                    reason: data.discard_reason || data.reason || 'Bulk discard',
                  })
                );

                if (result.error) {
                  throw result.error;
                }

                return result.data;
              },
              {
                maxRetries: 3,
                initialDelay: 1000,
                maxDelay: 10000,
              }
            );

            console.log('✅ Enhanced bulk discard successful:', result);

            return { data: result };
          }
        } catch (error) {
          console.error('❌ Enhanced bulk discard failed:', error);

          // Enhanced error messaging
          let errorMessage = 'An unexpected error occurred';

          if (error.status === 404) {
            errorMessage = 'Group not found - it may have been already processed';
          } else if (error.status === 400 && error.message?.includes('100 alerts')) {
            errorMessage =
              'Too many alerts selected. This has been automatically chunked - please try again.';
          } else if (error.message?.includes('Group validation failed')) {
            errorMessage = error.message;
          }

          return {
            error: {
              status: error.status || 'CUSTOM_ERROR',
              data: error.data || { error: errorMessage },
              message: errorMessage,
            },
          };
        }
      },
      invalidatesTags: ['SuppressedAlertGroups', 'Alert', 'SuppressedGroupDetail'],
    }),
    enhancedSuppressedGroupBulkAction: builder.mutation({
      queryFn: async (data, { dispatch, getState }) => {
        const { chunkArrayForBulkAction, processChunksSequentially, retryWithExponentialBackoff } =
          await import('../../portals/bureau/utils/chunkArrayBySize');

        try {
          console.log('🔄 Starting enhanced suppressed group bulk action with data:', data);

          // Validate input data
          if (!data.alert_ids || !Array.isArray(data.alert_ids) || data.alert_ids.length === 0) {
            throw new Error('Valid alert_ids array is required for enhanced bulk action');
          }

          const alertIds = data.alert_ids;

          // Check if we need chunking
          if (alertIds.length > 100) {
            console.log(`🔄 Processing ${alertIds.length} alerts in chunks (max 100 per chunk)`);

            // Chunk the alert IDs
            const chunks = chunkArrayForBulkAction(alertIds, 100);

            // Process chunks with retry logic
            const chunkResults = await processChunksSequentially(
              chunks,
              async (chunkAlertIds, chunkIndex) => {
                console.log(
                  `📦 Processing chunk ${chunkIndex + 1}/${chunks.length} with ${chunkAlertIds.length} alerts`
                );

                return await retryWithExponentialBackoff(
                  async () => {
                    const result = await dispatch(
                      apiSlice.endpoints.suppressedGroupBulkAction.initiate({
                        ...data,
                        alert_ids: chunkAlertIds,
                        reason: `${data.reason || 'Bulk action'} (chunk ${chunkIndex + 1}/${chunks.length})`,
                      })
                    );

                    if (result.error) {
                      throw result.error;
                    }

                    return result.data;
                  },
                  {
                    maxRetries: 3,
                    initialDelay: 1000,
                    maxDelay: 10000,
                  }
                );
              },
              {
                onProgress: (progressInfo) => {
                  console.log(
                    `📈 Chunk progress: ${progressInfo.progress.toFixed(1)}% (${progressInfo.chunkIndex}/${progressInfo.totalChunks})`
                  );
                },
                onChunkError: (error, chunkIndex, chunk) => {
                  console.error(`❌ Error processing chunk ${chunkIndex + 1}:`, error);
                },
                continueOnError: true,
              }
            );

            console.log('✅ Chunked bulk action completed:', {
              totalChunks: chunkResults.totalChunks,
              successfulChunks: chunkResults.successfulChunks,
              failedChunks: chunkResults.failedChunks,
              totalAlerts: alertIds.length,
            });

            // Return summary of chunked operation
            return {
              data: {
                success: true,
                chunked: true,
                ...chunkResults,
                totalAlertsProcessed: alertIds.length,
              },
            };
          } else {
            // Process normally for small groups with retry
            const result = await retryWithExponentialBackoff(
              async () => {
                const result = await dispatch(
                  apiSlice.endpoints.suppressedGroupBulkAction.initiate(data)
                );

                if (result.error) {
                  throw result.error;
                }

                return result.data;
              },
              {
                maxRetries: 3,
                initialDelay: 1000,
                maxDelay: 10000,
              }
            );

            console.log('✅ Enhanced bulk action successful:', result);

            return { data: result };
          }
        } catch (error) {
          console.error('❌ Enhanced bulk action failed:', error);

          // Enhanced error messaging
          let errorMessage = 'An unexpected error occurred';

          if (error.status === 404) {
            errorMessage = 'Group not found - it may have been already processed';
          } else if (error.status === 400 && error.message?.includes('100 alerts')) {
            errorMessage =
              'Too many alerts selected. This has been automatically chunked - please try again.';
          } else if (error.message?.includes('Valid alert_ids array is required')) {
            errorMessage = error.message;
          }

          return {
            error: {
              status: error.status || 'CUSTOM_ERROR',
              data: error.data || { error: errorMessage },
              message: errorMessage,
            },
          };
        }
      },
      invalidatesTags: ['SuppressedAlertGroups', 'Alert', 'SuppressedGroupDetail'],
    }),
    validateSuppressedGroup: builder.query({
      query: (groupKey) => ({
        url: `monitoring/suppressed-alerts/groups/${encodeURIComponent(groupKey)}/`,
        method: 'GET',
      }),
      providesTags: (result, error, groupKey) => [{ type: 'SuppressedGroupDetail', id: groupKey }],
      transformResponse: (response) => {
        return {
          valid: true,
          data: response,
          alertCount: response.alerts?.length || 0,
        };
      },
      transformErrorResponse: (response) => {
        if (response.status === 404) {
          return {
            valid: false,
            error: 'Group not found - it may have been processed or the data is stale',
            shouldRefresh: true,
            status: response.status,
          };
        }
        return {
          valid: false,
          error: `Validation failed: ${response.status} ${response.data?.error || 'Unknown error'}`,
          shouldRefresh: false,
          status: response.status,
        };
      },
    }),
    validateSuppressedAlerts: builder.mutation({
      query: (data) => ({
        url: 'monitoring/suppressed-alerts/validate/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response) => {
        console.log('🔍 Alert validation result:', response);
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ Alert validation failed:', response);
        return {
          error: `Validation failed: ${response.status} ${response.data?.error || 'Unknown error'}`,
          status: response.status,
        };
      },
    }),

    // ========== OPERATIONS ENDPOINTS (operations/) ==========
    submitFeatureRequest: builder.mutation({
      query: (data) => ({
        url: 'operations/feature-request/',
        method: 'POST',
        body: data,
      }),
    }),
    fetchBranches: builder.query({
      query: () => 'operations/branches/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['Branches'],
    }),
    fetchBranch: builder.query({
      query: (id) => `operations/branches/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Branches', id }],
    }),
    fetchTechnicians: builder.query({
      query: () => 'operations/technicians/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['Technicians'],
    }),
    getTasks: builder.query({
      query: () => 'operations/tasks/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['Task'],
    }),
    getTask: builder.query({
      query: (id) => `operations/tasks/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),
    addTask: builder.mutation({
      query: (task) => ({
        url: 'operations/tasks/',
        method: 'POST',
        body: task,
      }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),
    updateTask: builder.mutation({
      query: ({ id, ...task }) => ({
        url: `operations/tasks/${id}/`,
        method: 'PUT',
        body: task,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Task', id }],
    }),
    deleteTask: builder.mutation({
      query: (id) => ({
        url: `operations/tasks/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),
    createBranch: builder.mutation({
      query: (data) => ({
        url: 'operations/branches/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Branches'],
    }),
    updateBranch: builder.mutation({
      query: ({ id, data }) => ({
        url: `operations/branches/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Branches', id }, 'Branches'],
    }),
    deleteBranch: builder.mutation({
      query: (id) => ({
        url: `operations/branches/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Branches'],
    }),
    createTechnician: builder.mutation({
      query: (data) => ({
        url: 'operations/technicians/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Technicians'],
    }),
    updateTechnician: builder.mutation({
      query: ({ id, data }) => ({
        url: `operations/technicians/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Technicians'],
    }),
    deleteTechnician: builder.mutation({
      query: (id) => ({
        url: `operations/technicians/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Technicians'],
    }),
    fetchSalesReps: builder.query({
      query: () => 'operations/sales-reps/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['SalesReps'],
    }),
    fetchSalesRep: builder.query({
      query: (id) => `operations/sales-reps/${id}/`,
      providesTags: (result, error, id) => [{ type: 'SalesReps', id }],
    }),
    createSalesRep: builder.mutation({
      query: (data) => ({
        url: 'operations/sales-reps/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SalesReps'],
    }),
    updateSalesRep: builder.mutation({
      query: ({ id, data }) => ({
        url: `operations/sales-reps/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'SalesReps', id }, 'SalesReps'],
    }),
    deleteSalesRep: builder.mutation({
      query: (id) => ({
        url: `operations/sales-reps/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SalesReps'],
    }),

    // Sales team members (real users with assigned_portal='sales')
    fetchSalesTeamMembers: builder.query({
      query: (params) => {
        if (params?.branch_id) {
          return `fleet/sales/team/?branch_id=${params.branch_id}`;
        }
        return 'fleet/sales/team/';
      },
      providesTags: ['SalesTeam'],
    }),

    // ========== TESTING ENDPOINTS (testing/) ==========
    fetchTests: builder.query({
      query: () => 'testing/test-list/',
      providesTags: ['Test'],
    }),
    fetchTestDetail: builder.query({
      query: ({ testId, testType }) => `testing/test-detail/${testId}/${testType}/`,
      providesTags: (result, error, { testId }) => [{ type: 'Test', id: testId }],
    }),
    fetchRecentTestActivity: builder.query({
      query: (limit = 10) => `testing/recent-activity/?limit=${limit}`,
      providesTags: ['Test'],
    }),
    createNewInstallTest: builder.mutation({
      query: (installTestData) => ({
        url: 'testing/new-install-test/',
        method: 'POST',
        body: installTestData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle', 'DeviceGroup'],
    }),
    createNewReInstallTest: builder.mutation({
      query: (reinstallTestData) => ({
        url: 'testing/re-install-test/',
        method: 'POST',
        body: reinstallTestData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle', 'DeviceGroup', 'Test'],
    }),
    createNewRepairTest: builder.mutation({
      query: (repairTestData) => ({
        url: 'testing/new-repair-test/',
        method: 'POST',
        body: repairTestData,
      }),
      invalidatesTags: ['Device'],
    }),
    createNewDeInstallTest: builder.mutation({
      query: (deinstallTestData) => ({
        url: 'testing/new-deinstall-test/',
        method: 'POST',
        body: deinstallTestData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),

    // ========== TEMP TESTS ENDPOINTS (testing/temp-tests/) ==========
    fetchTempTests: builder.query({
      query: () => 'testing/temp-tests/',
      providesTags: ['TempTest'],
    }),
    createTempTest: builder.mutation({
      query: (tempTestData) => ({
        url: 'testing/temp-tests/',
        method: 'POST',
        body: tempTestData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['TempTest', 'Test'],
    }),
    updateTempTest: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `testing/temp-tests/${id}/`,
        method: 'PUT',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['TempTest', 'Test'],
    }),
    deleteTempTest: builder.mutation({
      query: (id) => ({
        url: `testing/temp-tests/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TempTest', 'Test'],
    }),

    // ========== DEVICE STATE ANALYSIS (for CMS workflow mode) ==========
    analyzeDeviceState: builder.query({
      query: (deviceIdentifier) => ({
        url: `testing/device-state-analysis/${deviceIdentifier}/`,
        method: 'GET',
      }),
      providesTags: (result, error, deviceIdentifier) => [
        { type: 'DeviceState', id: deviceIdentifier },
      ],
    }),

    // ========== UNIFIED TEST WIZARD ENDPOINTS ==========
    submitUnifiedTest: builder.mutation({
      query: (testData) => ({
        url: 'testing/submit-test/',
        method: 'POST',
        body: testData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Tests', { type: 'DeviceGroup', id: 'LIST' }],
    }),
    vehicleAnalysis: builder.query({
      query: (vehicleId) => ({
        url: `testing/vehicle-analysis/${vehicleId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, vehicleId) => [
        { type: 'VehicleAnalysis', id: vehicleId },
      ],
    }),

    // ========== CLIENT ENDPOINTS (clients/) ==========
    createClient: builder.mutation({
      query: (clientData) => ({
        url: 'clients/create-client/',
        method: 'POST',
        body: clientData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Client'],
    }),
    fetchClient: builder.query({
      query: (group_id) => `clients/client-details/${group_id}/`,
      providesTags: ['Client'],
    }),
    fetchClientByDevice: builder.query({
      query: (device_id) => `clients/client-details-by-device/${device_id}/`,
      providesTags: ['Client'],
    }),

    // ========== AUTHENTICATION ENDPOINTS (unchanged) ==========
    createUserGroup: builder.mutation({
      query: (groupData) => ({
        url: '/auth/groups/',
        method: 'POST',
        body: groupData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['UserGroup'],
    }),
    updateUserGroup: builder.mutation({
      query: ({ groupId, ...groupData }) => ({
        url: `/auth/groups/${groupId}/`,
        method: 'PUT',
        body: groupData,
      }),
      invalidatesTags: ['UserGroup'],
    }),
    deleteUserGroup: builder.mutation({
      query: (groupId) => ({
        url: `/auth/groups/${groupId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserGroup'],
    }),
    fetchUsers: builder.query({
      query: () => '/auth/users/',
      providesTags: ['User'],
    }),
    createUser: builder.mutation({
      query: (userData) => ({
        url: '/auth/users/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation({
      query: ({ userId, ...userData }) => ({
        url: `/auth/users/${userId}/`,
        method: 'PUT',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/auth/users/${userId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    resetPassword: builder.mutation({
      query: ({ username, email, newPassword }) => ({
        url: '/auth/reset-password/',
        method: 'POST',
        body: { username, email, newPassword },
      }),
    }),

    // ========== PRESENCE & ACTIVITY ENDPOINTS (v1/rbac/presence/) ==========
    fetchBureauAgentPresence: builder.query({
      query: () => 'v1/rbac/presence/bureau/agents/',
      providesTags: ['AgentPresence'],
      pollingInterval: 30000, // Poll every 30 seconds
    }),
    fetchAgentPresenceSummary: builder.query({
      query: (portal = 'bureau') => `v1/rbac/presence/bureau/summary/?portal=${portal}`,
      providesTags: ['AgentPresenceSummary'],
      pollingInterval: 30000, // Poll every 30 seconds for presence stats
    }),
    fetchQuickPresenceStats: builder.query({
      query: (portal = 'bureau') => `v1/rbac/presence/bureau/quick/?portal=${portal}`,
      providesTags: ['QuickPresenceStats'],
      pollingInterval: 30000, // Poll every 30 seconds
    }),
    fetchUserActivityDetails: builder.query({
      query: ({ userId, portal = 'bureau' }) => `v1/rbac/presence/user/${userId}/?portal=${portal}`,
      providesTags: (result, error, { userId }) => [{ type: 'UserActivity', id: userId }],
    }),
    updateUserActivity: builder.mutation({
      query: (activityData) => ({
        url: 'v1/rbac/presence/activity/update/',
        method: 'POST',
        body: activityData,
      }),
      invalidatesTags: ['AgentPresence', 'AgentPresenceSummary', 'QuickPresenceStats'],
    }),
    clearUserPresence: builder.mutation({
      query: () => ({
        url: 'v1/rbac/presence/activity/clear/',
        method: 'POST',
      }),
      invalidatesTags: ['AgentPresence', 'AgentPresenceSummary', 'QuickPresenceStats'],
    }),
    fetchActivityTimeline: builder.query({
      query: ({ portal = 'bureau', hours = 24 }) =>
        `v1/rbac/presence/timeline/?portal=${portal}&hours=${hours}`,
      providesTags: ['ActivityTimeline'],
    }),

    // ========== USER AUDIT LOG ENDPOINTS ==========
    fetchUserAuditLogs: builder.query({
      query: ({ userId, ...params }) => {
        const queryString = new URLSearchParams(params).toString();
        return `v1/rbac/audit-logs/user/${userId}/?${queryString}`;
      },
      providesTags: (result, error, { userId }) => [{ type: 'UserAuditLogs', id: userId }],
    }),
    clearUserAuditLogs: builder.mutation({
      query: ({ userId, clearType, startDate, endDate }) => ({
        url: `v1/rbac/audit-logs/${userId}/clear/`,
        method: 'POST',
        body: { clear_type: clearType, start_date: startDate, end_date: endDate },
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'UserAuditLogs', id: userId }],
    }),
    fetchUserActivityStatus: builder.query({
      query: (userId) => `v1/rbac/users/${userId}/activity-status/`,
      providesTags: (result, error, userId) => [{ type: 'UserActivityStatus', id: userId }],
      pollingInterval: 30000, // Poll every 30 seconds
    }),
    fetchUsersWithActivity: builder.query({
      query: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return `v1/rbac/users/with-activity/?${queryString}`;
      },
      providesTags: ['UsersWithActivity'],
      pollingInterval: 30000, // Poll every 30 seconds for real-time status
    }),
  }),
});

export const {
  // Fleet Management Hooks
  useFetchDeviceGroupsQuery,
  useFetchVehiclesForGroupsQuery,
  useFetchDevicesForVehiclesQuery,
  useFetchGroupVehiclesQuery,
  useFetchUnassignedDevicesQuery,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useMoveDeviceToGroupMutation,
  useMoveDeviceToGroupByPkMutation,
  useFetchCommsDevicesQuery,
  useUpdateCommsDeviceMutation,
  useFetchCommsExportCatalogueQuery,
  useExportCommsDevicesMutation,
  useCreateNewDeviceMutation,
  useClientOnboardingMutation,
  useCreateGroupMutation,
  useFetchCmsParentOrgsQuery,
  useCreateLocalGroupMutation,
  useAssignDeviceToGroupMutation,
  useUpdateVehicleMutation,
  useEditVehicleMutation,
  useDeleteVehicleMutation,
  useUpdateDeviceMutation,
  useUpdateDeviceExternalFieldsMutation,
  useLocalAddDevicesMutation,
  useCreateDeviceSameGroupMutation,
  useAssociateDeviceWithVehicleMutation,
  useCreateVehicleMutation,
  useAssignDeviceToVehicleMutation,
  useFetchGeofencesQuery,
  useCreateGeofenceMutation,
  useUpdateGeofenceMutation,
  useDeleteGeofenceMutation,
  useFetchVehicleEventHistoryQuery,

  // Dynamic Wizard Device Management Hooks
  useFetchAvailableDevicesQuery,
  useQuickCreateDeviceMutation,
  useTransferDeviceMutation,
  useMoveDeviceToDeinstalledMutation,
  useDeinstallDeviceMutation,
  useDeleteDeviceMutation,
  useBulkDeviceOperationMutation,

  // Monitoring Hooks
  useFetchAlertsQuery,
  useFetchAlertsByDeviceQuery,
  useCreateEventMutation,
  useFetchEventsQuery,
  useFetchEventDetailsQuery,
  useResolveEventMutation,
  useAssignEventMutation,
  useUpdateEventMutation,

  useResolveAlertMutation,
  useUpdateAlertStatusMutation,
  useInvestigateAlertMutation,
  useDiscardAlertConsolidatedMutation,
  useBulkAlertActionMutation,

  // Suppressed Alerts Hooks
  useFetchSuppressedAlertsQuery,
  useFetchSuppressedAlertDetailsQuery,
  useUnsuppressAlertMutation,
  useResetSuppressionGroupMutation,
  useResolveSupressedAlertMutation,

  // Enhanced Suppression Management Hooks
  useFetchUserSuppressionProfilesQuery,
  useFetchUserSuppressionProfileQuery,
  useCreateUserSuppressionProfileMutation,
  useUpdateUserSuppressionProfileMutation,
  useDeleteUserSuppressionProfileMutation,
  useFetchDeviceSuppressionOverridesQuery,
  useFetchDeviceSuppressionOverrideQuery,
  useCreateDeviceSuppressionOverrideMutation,
  useUpdateDeviceSuppressionOverrideMutation,
  useDeleteDeviceSuppressionOverrideMutation,
  useFetchSuppressionEffectivenessStatsQuery,
  useFetchSuppressionStatsQuery,

  // Unified Suppression System Hooks
  useFetchSuppressionProfilesQuery,
  useActivateSuppressionProfileMutation,
  useFetchUnifiedSuppressionStatsQuery,
  useFetchSuppressionHealthQuery,
  useTestSuppressionRulesMutation,
  useFetchSuppressionDecisionLogsQuery,

  // Global Suppression Profile Hooks
  useFetchGlobalSuppressionProfilesQuery,
  useFetchGlobalSuppressionProfileQuery,
  useUpdateGlobalSuppressionProfileMutation,
  useSetGlobalDefaultProfileMutation,

  // Alert Disable Hooks
  useFetchAlertDisablesQuery,
  useFetchAlertDisableQuery,
  useCreateAlertDisableMutation,
  useUpdateAlertDisableMutation,
  useDeleteAlertDisableMutation,

  // Alert Types Hook
  useFetchAlertTypesQuery,

  // VMS Alerts Hooks
  useFetchVMSAlertsQuery,
  useFetchVMSAlertDetailQuery,

  // Alert Grouping Hooks
  useFetchRecentlyDiscardedAlertsQuery,
  useFetchSuppressedAlertGroupsQuery,
  useFetchSuppressedGroupDetailQuery,
  useSuppressedGroupBulkActionMutation,
  useEnhancedSuppressedGroupBulkActionMutation,
  useBulkDiscardSuppressedGroupMutation,
  useEnhancedBulkDiscardSuppressedGroupMutation,
  useValidateSuppressedGroupQuery,
  useValidateSuppressedAlertsMutation,

  // Operations Hooks
  useSubmitFeatureRequestMutation,
  useFetchBranchesQuery,
  useFetchBranchQuery,
  useFetchTechniciansQuery,
  useGetTasksQuery,
  useGetTaskQuery,
  useAddTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useCreateTechnicianMutation,
  useUpdateTechnicianMutation,
  useDeleteTechnicianMutation,
  useFetchSalesRepsQuery,
  useFetchSalesRepQuery,
  useCreateSalesRepMutation,
  useUpdateSalesRepMutation,
  useFetchSalesTeamMembersQuery,
  useDeleteSalesRepMutation,

  // Testing Hooks
  useFetchTestsQuery,
  useFetchTestDetailQuery,
  useFetchRecentTestActivityQuery,
  useCreateNewInstallTestMutation,
  useCreateNewReInstallTestMutation,
  useCreateNewRepairTestMutation,
  useCreateNewDeInstallTestMutation,

  // Temp Test Hooks
  useFetchTempTestsQuery,
  useCreateTempTestMutation,
  useUpdateTempTestMutation,
  useDeleteTempTestMutation,

  // Device State Analysis (CMS workflow mode)
  useAnalyzeDeviceStateQuery,

  // Unified Test Wizard Hooks
  useSubmitUnifiedTestMutation,
  useVehicleAnalysisQuery,

  // Client Hooks
  useCreateClientMutation,
  useFetchClientQuery,
  useFetchClientByDeviceQuery,

  // Authentication Hooks
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
  useDeleteUserGroupMutation,
  useFetchUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetPasswordMutation,

  // Presence & Activity Hooks
  useFetchBureauAgentPresenceQuery,
  useFetchAgentPresenceSummaryQuery,
  useFetchQuickPresenceStatsQuery,
  useFetchUserActivityDetailsQuery,
  useUpdateUserActivityMutation,
  useClearUserPresenceMutation,
  useFetchActivityTimelineQuery,

  // User Audit and Activity Hooks
  useFetchUserAuditLogsQuery,
  useClearUserAuditLogsMutation,
  useFetchUserActivityStatusQuery,
  useFetchUsersWithActivityQuery,
} = apiSlice;
